import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Checkbox } from '../../components/atoms/Checkbox'
import { Input } from '../../components/atoms/Input'
import { Modal } from '../../components/atoms/Modal'
import { Select } from '../../components/atoms/Select'
import { Textarea } from '../../components/atoms/Textarea'
import { Field } from '../../components/molecules/Field'
import type {
  CategoryTreeAttribute,
  CategoryTreeNode,
  CreateProductInput,
  MediaLinkUpdate,
  NewMediaSlot,
  ProductAttributeCustomValueDto,
  ProductAttributeValueDto,
  ProductDto,
  ProductMediaDto,
  UpdateProductInput
} from '../../../viewmodels/useProducts'

const NAME_MAX = 128
const DESC_MAX = 2048
const VALUE_MAX = 1024
const ALT_TEXT_MAX = 256
const CUSTOM_DATATYPE_NAME = 'custom'
const MEDIA_MAX_ACTIVE = 10
const IMAGE_MAX_BYTES = 5 * 1024 * 1024
const VIDEO_MAX_BYTES = 50 * 1024 * 1024
const IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp'])
const VIDEO_TYPES = new Set(['video/mp4'])
const ACCEPT_TYPES = [...IMAGE_TYPES, ...VIDEO_TYPES].join(',')

interface Props {
  readonly open: boolean
  readonly mode: 'create' | 'edit'
  readonly initial: ProductDto | null
  readonly categoryTree: ReadonlyArray<CategoryTreeNode>
  readonly onClose: () => void
  readonly onSubmit: (
    input: CreateProductInput | UpdateProductInput
  ) => Promise<{ ok: true } | { ok: false; field?: string | null; message: string }>
  /**
   * Edit-mode only. Triggered by the "Propagate variants" button in
   * the axes section. Reconciles product_variants vs the product's
   * saved is_axes=1 CDT selections. The modal disables the button
   * after a successful click; it re-enables when the form's axes
   * selections drift from the propagated set again.
   */
  readonly onPropagate?: (productId: string) => Promise<{ ok: true } | { ok: false; message: string }>
}

interface NewSlot {
  readonly localId: string
  readonly file: File
  readonly contentType: string
  readonly sizeBytes: number
  alt_text: string | null
  sort_order: number
  is_thumbnail: boolean
  width: number | null
  height: number | null
  readonly originalName: string
  readonly previewUrl: string
}

interface ExistingEdit {
  alt_text?: string | null
  sort_order?: number
  is_thumbnail?: boolean
}

interface FormState {
  name: string
  description: string
  category_id: string | null
  /** Order rules — per-line purchase cap. null = no limit. */
  max_order_quantity: number | null
  /** Customer-facing visibility. true (default) = product appears in
   *  Shop + Build pickers. false = operator-only (component-only). */
  display_in_catalog: boolean
  values: Record<string, string>
  customValues: Record<string, Set<string>>
  /** Snapshot of media as it came in. Never mutated. */
  existingMedia: ProductMediaDto[]
  /** Per-link metadata edits keyed by link_id. */
  existingEdits: Record<number, ExistingEdit>
  /** Set of link_ids the operator removed. */
  deletedLinkIds: Set<number>
  /** New files queued for upload on submit. */
  newMedia: NewSlot[]
}

interface EffectiveSlot {
  readonly key: string
  readonly kind: 'existing' | 'new'
  readonly linkId: number | null
  readonly localId: string | null
  readonly previewUrl: string
  readonly contentType: string
  readonly sizeBytes: number
  readonly width: number | null
  readonly height: number | null
  readonly altText: string | null
  readonly sortOrder: number
  readonly isThumbnail: boolean
  readonly isVideo: boolean
  readonly originalName: string | null
}

const EMPTY: FormState = {
  name: '',
  description: '',
  category_id: null,
  max_order_quantity: null,
  display_in_catalog: true,
  values: {},
  customValues: {},
  existingMedia: [],
  existingEdits: {},
  deletedLinkIds: new Set<number>(),
  newMedia: []
}

/**
 * ProductFormModal — Create + Edit.
 *
 * Two layers:
 *   1. Product fields: name, description, category, dynamic per-
 *      attribute inputs driven by the category's attribute tree.
 *   2. Media: existing thumbnails + alt-text + thumbnail toggle +
 *      reorder + delete; plus a file picker to add new media.
 *
 * Submit composes a multipart payload via useProducts.create/update,
 * per-table dirty tracking (decision 25): the modal sends only the
 * sections that actually changed.
 */
export function ProductFormModal({
  open,
  mode,
  initial,
  categoryTree,
  onClose,
  onSubmit,
  onPropagate
}: Props): JSX.Element {
  const [state, setState] = useState<FormState>(EMPTY)
  const [submitting, setSubmitting] = useState(false)
  const [propagateBusy, setPropagateBusy] = useState(false)
  const [propagateError, setPropagateError] = useState<string | null>(null)
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  // Track preview URLs so we can revoke them when the modal closes.
  const newMediaUrlsRef = useRef<string[]>([])

  useEffect(() => {
    if (!open) {
      // Revoke object URLs when the modal goes away.
      for (const u of newMediaUrlsRef.current) URL.revokeObjectURL(u)
      newMediaUrlsRef.current = []
      return
    }
    if (initial === null) {
      setState({ ...EMPTY, values: {}, customValues: {}, existingMedia: [], existingEdits: {}, deletedLinkIds: new Set(), newMedia: [] })
    } else {
      const values: Record<string, string> = {}
      for (const v of initial.attribute_values) values[v.attribute_id] = v.value
      const customValues: Record<string, Set<string>> = {}
      for (const cv of initial.attribute_custom_values) {
        customValues[cv.attribute_id] = new Set(cv.custom_data_type_value_ids)
      }
      setState({
        name: initial.name,
        description: initial.description,
        category_id: initial.category_id,
        max_order_quantity: initial.max_order_quantity,
        display_in_catalog: initial.display_in_catalog,
        values,
        customValues,
        existingMedia: initial.media,
        existingEdits: {},
        deletedLinkIds: new Set<number>(),
        newMedia: []
      })
    }
    setFieldError(null)
    setFormError(null)
    setSubmitting(false)
    setPropagateError(null)
    // dep on initial?.id (not initial): a successful Propagate updates
    // the row's reference but keeps the same id. We want the modal to
    // keep operator-typed form state intact while
    // `initial.propagated_axes_selections` (read directly by the dirty
    // check) updates from the new prop value.
  }, [open, initial?.id])

  const sortedCategories = useMemo(
    () => [...categoryTree].sort((a, b) => a.name.localeCompare(b.name)),
    [categoryTree]
  )
  const currentCategory = useMemo(
    () => categoryTree.find((c) => c.id === state.category_id) ?? null,
    [categoryTree, state.category_id]
  )

  const effectiveMedia = useMemo<EffectiveSlot[]>(
    () => computeEffectiveMedia(state),
    [state]
  )
  const activeMediaCount = effectiveMedia.length
  const atMediaCap = activeMediaCount >= MEDIA_MAX_ACTIVE

  // Axes attributes (is_axes=1) get a dedicated section + the Propagate
  // button. The rest of the category's attributes render in the main
  // CategoryAttributesSection. Both are derived from the same category.
  const axesAttributes = useMemo<CategoryTreeAttribute[]>(
    () =>
      currentCategory === null
        ? []
        : currentCategory.attributes.filter((a) => a.custom_data_type_is_axes === true),
    [currentCategory]
  )
  const nonAxesAttributes = useMemo<CategoryTreeAttribute[]>(
    () =>
      currentCategory === null
        ? []
        : currentCategory.attributes.filter((a) => a.custom_data_type_is_axes !== true),
    [currentCategory]
  )

  // Propagate-needed = the form's axes selections differ from the
  // server's currently-propagated set. After a successful propagate,
  // `initial.propagated_axes_selections` updates and the comparison
  // flips back to "in sync" (assuming the operator didn't keep editing).
  const propagateNeeded = useMemo<boolean>(() => {
    if (mode !== 'edit' || initial === null) return false
    if (axesAttributes.length === 0) return false
    const propMap = new Map<string, Set<string>>()
    for (const p of initial.propagated_axes_selections) {
      propMap.set(p.attribute_id, new Set(p.custom_data_type_value_ids))
    }
    for (const attr of axesAttributes) {
      const formSet = state.customValues[attr.id] ?? new Set<string>()
      const propSet = propMap.get(attr.id) ?? new Set<string>()
      if (formSet.size !== propSet.size) return true
      for (const v of formSet) {
        if (!propSet.has(v)) return true
      }
    }
    return false
  }, [mode, initial, axesAttributes, state.customValues])

  const handlePropagate = async (): Promise<void> => {
    if (mode !== 'edit' || initial === null || onPropagate === undefined) return
    setPropagateBusy(true)
    setPropagateError(null)
    const res = await onPropagate(initial.id)
    setPropagateBusy(false)
    if (!res.ok) setPropagateError(res.message)
  }

  /* ── product-field handlers ── */
  const onCategoryChange = (next: string | null): void => {
    setState((s) => ({ ...s, category_id: next, values: {}, customValues: {} }))
    setFieldError(null)
  }
  const setValue = (attributeId: string, value: string): void => {
    setState((s) => ({ ...s, values: { ...s.values, [attributeId]: value } }))
  }
  const toggleCustomValue = (attributeId: string, valueId: string): void => {
    setState((s) => {
      const existing = s.customValues[attributeId] ?? new Set<string>()
      const next = new Set(existing)
      if (next.has(valueId)) next.delete(valueId)
      else next.add(valueId)
      return { ...s, customValues: { ...s.customValues, [attributeId]: next } }
    })
  }

  /* ── media handlers ── */
  const handleAddFiles = async (files: FileList | null): Promise<void> => {
    if (files === null || files.length === 0) return
    setFieldError(null)
    setFormError(null)
    const slotsToAdd: NewSlot[] = []
    let nextSort = highestSortOrder(state, effectiveMedia) + 1
    for (let i = 0; i < files.length; i++) {
      const f = files[i]
      if (f === undefined) continue
      if (activeMediaCount + slotsToAdd.length >= MEDIA_MAX_ACTIVE) {
        setFormError(`At most ${MEDIA_MAX_ACTIVE} media per product`)
        break
      }
      const ct = f.type
      if (!IMAGE_TYPES.has(ct) && !VIDEO_TYPES.has(ct)) {
        setFormError(`${f.name}: only JPEG/PNG/WebP images or MP4 videos`)
        continue
      }
      const cap = IMAGE_TYPES.has(ct) ? IMAGE_MAX_BYTES : VIDEO_MAX_BYTES
      if (f.size > cap) {
        setFormError(`${f.name}: ${formatBytes(f.size)} exceeds ${formatBytes(cap)} cap`)
        continue
      }
      const previewUrl = URL.createObjectURL(f)
      newMediaUrlsRef.current.push(previewUrl)
      const dims = IMAGE_TYPES.has(ct) ? await readImageDimensions(previewUrl) : { width: null, height: null }
      slotsToAdd.push({
        localId: crypto.randomUUID(),
        file: f,
        contentType: ct,
        sizeBytes: f.size,
        alt_text: null,
        sort_order: nextSort++,
        is_thumbnail: false,
        width: dims.width,
        height: dims.height,
        originalName: f.name,
        previewUrl
      })
    }
    if (slotsToAdd.length > 0) {
      setState((s) => {
        const haveAnyThumb = computeEffectiveMedia(s).some((m) => m.isThumbnail)
        const next = [...s.newMedia, ...slotsToAdd]
        if (!haveAnyThumb && next[0] !== undefined) {
          next[0] = { ...next[0], is_thumbnail: true }
        }
        return { ...s, newMedia: next }
      })
    }
    if (fileInputRef.current !== null) fileInputRef.current.value = ''
  }

  const onDeleteSlot = (slot: EffectiveSlot): void => {
    setFieldError(null)
    setFormError(null)
    if (slot.kind === 'existing' && slot.linkId !== null) {
      setState((s) => {
        const deletedLinkIds = new Set(s.deletedLinkIds)
        deletedLinkIds.add(slot.linkId as number)
        const existingEdits = { ...s.existingEdits }
        delete existingEdits[slot.linkId as number]
        return { ...s, deletedLinkIds, existingEdits }
      })
    } else if (slot.kind === 'new' && slot.localId !== null) {
      setState((s) => {
        const removed = s.newMedia.find((n) => n.localId === slot.localId)
        if (removed !== undefined) URL.revokeObjectURL(removed.previewUrl)
        return { ...s, newMedia: s.newMedia.filter((n) => n.localId !== slot.localId) }
      })
    }
  }

  const onAltChange = (slot: EffectiveSlot, value: string): void => {
    const trimmed = value === '' ? null : value
    if (slot.kind === 'existing' && slot.linkId !== null) {
      setState((s) => ({
        ...s,
        existingEdits: {
          ...s.existingEdits,
          [slot.linkId as number]: {
            ...(s.existingEdits[slot.linkId as number] ?? {}),
            alt_text: trimmed
          }
        }
      }))
    } else if (slot.kind === 'new' && slot.localId !== null) {
      setState((s) => ({
        ...s,
        newMedia: s.newMedia.map((n) => (n.localId === slot.localId ? { ...n, alt_text: trimmed } : n))
      }))
    }
  }

  const onSetThumbnail = (slot: EffectiveSlot): void => {
    setState((s) => {
      const existingEdits: Record<number, ExistingEdit> = {}
      // Clear thumbnail on every other existing slot.
      for (const e of s.existingMedia) {
        const edits = s.existingEdits[e.link_id] ?? {}
        const wasThumb = edits.is_thumbnail !== undefined ? edits.is_thumbnail : e.is_thumbnail
        const wantsThumb = slot.kind === 'existing' && slot.linkId === e.link_id
        if (wasThumb !== wantsThumb) {
          existingEdits[e.link_id] = { ...edits, is_thumbnail: wantsThumb }
        } else if (Object.keys(edits).length > 0) {
          existingEdits[e.link_id] = edits
        }
      }
      const newMedia = s.newMedia.map((n) => {
        const wantsThumb = slot.kind === 'new' && slot.localId === n.localId
        return n.is_thumbnail === wantsThumb ? n : { ...n, is_thumbnail: wantsThumb }
      })
      return { ...s, existingEdits, newMedia }
    })
  }

  const onMoveSlot = (slot: EffectiveSlot, direction: 'up' | 'down'): void => {
    const sortedEffective = effectiveMedia
    const idx = sortedEffective.findIndex((m) => m.key === slot.key)
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1
    if (idx < 0 || targetIdx < 0 || targetIdx >= sortedEffective.length) return
    const target = sortedEffective[targetIdx]
    if (target === undefined) return
    const slotSort = slot.sortOrder
    const targetSort = target.sortOrder
    setState((s) => applySortSwap(s, slot, target, slotSort, targetSort))
  }

  /* ── validation + submit ── */

  const clientValidate = (): { field: string; message: string } | null => {
    const name = state.name.trim()
    if (name.length < 1 || name.length > NAME_MAX) {
      return { field: 'name', message: `name must be 1..${NAME_MAX} chars` }
    }
    const desc = state.description.trim()
    if (desc.length < 1 || desc.length > DESC_MAX) {
      return { field: 'description', message: `description is required (max ${DESC_MAX} chars)` }
    }
    if (state.category_id === null || currentCategory === null) {
      return { field: 'category_id', message: 'category is required' }
    }
    for (const attr of currentCategory.attributes) {
      const isCustom = attr.datatype_name === CUSTOM_DATATYPE_NAME
      if (isCustom) {
        const selected = state.customValues[attr.id] ?? new Set<string>()
        if (selected.size === 0) {
          return { field: `attr:${attr.id}`, message: `select at least one value for '${attr.name}'` }
        }
      } else {
        const v = state.values[attr.id]?.trim() ?? ''
        if (v.length < 1) return { field: `attr:${attr.id}`, message: `'${attr.name}' is required` }
        if (v.length > VALUE_MAX) return { field: `attr:${attr.id}`, message: `'${attr.name}' must be ≤${VALUE_MAX} chars` }
      }
    }
    if (effectiveMedia.length === 0) {
      return { field: 'media', message: 'add at least one image' }
    }
    if (effectiveMedia.length > MEDIA_MAX_ACTIVE) {
      return { field: 'media', message: `at most ${MEDIA_MAX_ACTIVE} media per product` }
    }
    return null
  }

  const handleSubmit = async (): Promise<void> => {
    const clientErr = clientValidate()
    if (clientErr !== null) {
      setFieldError(clientErr)
      return
    }
    setFieldError(null)
    setFormError(null)
    setSubmitting(true)

    if (currentCategory === null || state.category_id === null) {
      setSubmitting(false)
      return
    }
    const attribute_values: ProductAttributeValueDto[] = []
    const attribute_custom_values: ProductAttributeCustomValueDto[] = []
    for (const attr of currentCategory.attributes) {
      if (attr.datatype_name === CUSTOM_DATATYPE_NAME) {
        const ids = state.customValues[attr.id]
        attribute_custom_values.push({
          attribute_id: attr.id,
          custom_data_type_value_ids: ids === undefined ? [] : [...ids].sort()
        })
      } else {
        attribute_values.push({
          attribute_id: attr.id,
          value: (state.values[attr.id] ?? '').trim()
        })
      }
    }

    const productPart = {
      name: state.name.trim(),
      description: state.description.trim(),
      category_id: state.category_id,
      max_order_quantity: state.max_order_quantity,
      display_in_catalog: state.display_in_catalog,
      attribute_values,
      attribute_custom_values
    }

    const newSlots: NewMediaSlot[] = state.newMedia
      .slice()
      .sort((a, b) => a.sort_order - b.sort_order || a.localId.localeCompare(b.localId))
      .map((n) => ({
        file: n.file,
        metadata: {
          alt_text: n.alt_text,
          sort_order: n.sort_order,
          is_thumbnail: n.is_thumbnail,
          width: n.width,
          height: n.height,
          original_name: n.originalName
        }
      }))

    let result: { ok: true } | { ok: false; field?: string | null; message: string }
    if (mode === 'create' || initial === null) {
      const payload: CreateProductInput = { product: productPart, media: newSlots }
      result = await onSubmit(payload)
    } else {
      const productDirty = isProductDirty(initial, productPart)
      const updates: MediaLinkUpdate[] = Object.entries(state.existingEdits)
        .map(([linkIdStr, e]) => {
          const link_id = Number(linkIdStr)
          const scratch: {
            link_id: number
            alt_text?: string | null
            sort_order?: number
            is_thumbnail?: boolean
          } = { link_id }
          if (e.alt_text !== undefined) scratch.alt_text = e.alt_text
          if (e.sort_order !== undefined) scratch.sort_order = e.sort_order
          if (e.is_thumbnail !== undefined) scratch.is_thumbnail = e.is_thumbnail
          return scratch
        })
        .filter((u) => Object.keys(u).length > 1)
      const deletes = [...state.deletedLinkIds]
      const payloadScratch: {
        product?: typeof productPart
        mediaNew?: NewMediaSlot[]
        mediaUpdates?: MediaLinkUpdate[]
        mediaDeletes?: number[]
      } = {}
      if (productDirty) payloadScratch.product = productPart
      if (newSlots.length > 0) payloadScratch.mediaNew = newSlots
      if (updates.length > 0) payloadScratch.mediaUpdates = updates
      if (deletes.length > 0) payloadScratch.mediaDeletes = deletes
      const payload: UpdateProductInput = payloadScratch
      if (Object.keys(payload).length === 0) {
        // Nothing dirty — close without a network call.
        setSubmitting(false)
        onClose()
        return
      }
      result = await onSubmit(payload)
    }

    setSubmitting(false)
    if (result.ok) {
      onClose()
      return
    }
    const f = result.field
    if (f === 'name' || f === 'description' || f === 'category_id' || f === 'media') {
      setFieldError({ field: f, message: result.message })
    } else {
      setFormError(result.message)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'create' ? 'Add product' : `Edit ${initial?.name ?? ''}`}
      maxWidth="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting ? 'Saving…' : mode === 'create' ? 'Add' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError !== null ? <Alert tone="error">{formError}</Alert> : null}

        <Field
          label="Name"
          error={fieldError?.field === 'name' ? fieldError.message : null}
        >
          <Input
            value={state.name}
            hasError={fieldError?.field === 'name'}
            onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
            placeholder="e.g. Bamboo Baby Rocker"
            maxLength={NAME_MAX}
          />
        </Field>

        <Field
          label="Description"
          error={fieldError?.field === 'description' ? fieldError.message : null}
        >
          <Textarea
            value={state.description}
            rows={3}
            hasError={fieldError?.field === 'description'}
            onChange={(e) => setState((s) => ({ ...s, description: e.target.value }))}
            placeholder="What the product is"
            maxLength={DESC_MAX}
          />
        </Field>

        <Field
          label="Category"
          helper="Picking a category reveals the attributes you need to fill in."
          error={fieldError?.field === 'category_id' ? fieldError.message : null}
        >
          <Select
            value={state.category_id ?? ''}
            hasError={fieldError?.field === 'category_id'}
            onChange={(e) => onCategoryChange(e.target.value === '' ? null : e.target.value)}
          >
            <option value="">— Select a category —</option>
            {sortedCategories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>

        <OrderRulesSection
          maxOrderQuantity={state.max_order_quantity}
          onChange={(next) => setState((s) => ({ ...s, max_order_quantity: next }))}
          error={fieldError?.field === 'max_order_quantity' ? fieldError.message : null}
        />

        <VisibilitySection
          displayInCatalog={state.display_in_catalog}
          onChange={(next) => setState((s) => ({ ...s, display_in_catalog: next }))}
        />

        {currentCategory !== null ? (
          <CategoryAttributesSection
            categoryName={currentCategory.name}
            attributes={nonAxesAttributes}
            values={state.values}
            customValues={state.customValues}
            fieldError={fieldError}
            onSetValue={setValue}
            onToggleCustomValue={toggleCustomValue}
          />
        ) : null}

        <MediaSection
          slots={effectiveMedia}
          atCap={atMediaCap}
          error={fieldError?.field === 'media' ? fieldError.message : null}
          fileInputRef={fileInputRef}
          onPick={(files) => void handleAddFiles(files)}
          onDelete={onDeleteSlot}
          onAltChange={onAltChange}
          onSetThumbnail={onSetThumbnail}
          onMove={onMoveSlot}
        />

        {mode === 'edit' && axesAttributes.length > 0 ? (
          <AxesSection
            attributes={axesAttributes}
            customValues={state.customValues}
            fieldError={fieldError}
            onToggleCustomValue={toggleCustomValue}
            propagateNeeded={propagateNeeded}
            propagateBusy={propagateBusy}
            propagateError={propagateError}
            onPropagate={() => void handlePropagate()}
            propagateDisabled={onPropagate === undefined}
          />
        ) : null}
      </div>
    </Modal>
  )
}

/* ───────────────── helpers ───────────────── */

function highestSortOrder(_state: FormState, effective: EffectiveSlot[]): number {
  return effective.length === 0
    ? -1
    : effective.reduce((max, m) => (m.sortOrder > max ? m.sortOrder : max), -1)
}

function computeEffectiveMedia(state: FormState): EffectiveSlot[] {
  const out: EffectiveSlot[] = []
  for (const e of state.existingMedia) {
    if (state.deletedLinkIds.has(e.link_id)) continue
    const edits = state.existingEdits[e.link_id] ?? {}
    out.push({
      key: `existing:${e.link_id}`,
      kind: 'existing',
      linkId: e.link_id,
      localId: null,
      previewUrl: e.gateway_path,
      contentType: e.content_type,
      sizeBytes: e.size_bytes,
      width: e.width,
      height: e.height,
      altText: edits.alt_text !== undefined ? edits.alt_text : e.alt_text,
      sortOrder: edits.sort_order !== undefined ? edits.sort_order : e.sort_order,
      isThumbnail: edits.is_thumbnail !== undefined ? edits.is_thumbnail : e.is_thumbnail,
      isVideo: e.content_type.startsWith('video/'),
      originalName: e.original_name
    })
  }
  for (const n of state.newMedia) {
    out.push({
      key: `new:${n.localId}`,
      kind: 'new',
      linkId: null,
      localId: n.localId,
      previewUrl: n.previewUrl,
      contentType: n.contentType,
      sizeBytes: n.sizeBytes,
      width: n.width,
      height: n.height,
      altText: n.alt_text,
      sortOrder: n.sort_order,
      isThumbnail: n.is_thumbnail,
      isVideo: n.contentType.startsWith('video/'),
      originalName: n.originalName
    })
  }
  return out.sort((a, b) => a.sortOrder - b.sortOrder || a.key.localeCompare(b.key))
}

function applySortSwap(
  s: FormState,
  slot: EffectiveSlot,
  target: EffectiveSlot,
  slotSort: number,
  targetSort: number
): FormState {
  let existingEdits = s.existingEdits
  if (slot.kind === 'existing' && slot.linkId !== null) {
    const prior = existingEdits[slot.linkId] ?? {}
    existingEdits = { ...existingEdits, [slot.linkId]: { ...prior, sort_order: targetSort } }
  }
  if (target.kind === 'existing' && target.linkId !== null) {
    const prior = existingEdits[target.linkId] ?? {}
    existingEdits = { ...existingEdits, [target.linkId]: { ...prior, sort_order: slotSort } }
  }
  const newMedia = s.newMedia.map((n) => {
    if (slot.kind === 'new' && slot.localId === n.localId) return { ...n, sort_order: targetSort }
    if (target.kind === 'new' && target.localId === n.localId) return { ...n, sort_order: slotSort }
    return n
  })
  return { ...s, existingEdits, newMedia }
}

function isProductDirty(
  initial: ProductDto,
  next: {
    name: string
    description: string
    category_id: string
    max_order_quantity: number | null
    display_in_catalog: boolean
    attribute_values: ProductAttributeValueDto[]
    attribute_custom_values: ProductAttributeCustomValueDto[]
  }
): boolean {
  if (initial.name !== next.name) return true
  if (initial.description !== next.description) return true
  if (initial.category_id !== next.category_id) return true
  if (initial.max_order_quantity !== next.max_order_quantity) return true
  if (initial.display_in_catalog !== next.display_in_catalog) return true
  if (initial.attribute_values.length !== next.attribute_values.length) return true
  const initValMap = new Map(initial.attribute_values.map((v) => [v.attribute_id, v.value]))
  for (const v of next.attribute_values) {
    if (initValMap.get(v.attribute_id) !== v.value) return true
  }
  if (initial.attribute_custom_values.length !== next.attribute_custom_values.length) return true
  const initCustomMap = new Map(
    initial.attribute_custom_values.map((cv) => [cv.attribute_id, [...cv.custom_data_type_value_ids].sort().join(',')])
  )
  for (const cv of next.attribute_custom_values) {
    const sig = [...cv.custom_data_type_value_ids].sort().join(',')
    if (initCustomMap.get(cv.attribute_id) !== sig) return true
  }
  return false
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

async function readImageDimensions(url: string): Promise<{ width: number | null; height: number | null }> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = (): void => resolve({ width: img.naturalWidth, height: img.naturalHeight })
    img.onerror = (): void => resolve({ width: null, height: null })
    img.src = url
  })
}

/* ───────────────── presentational pieces ───────────────── */

/**
 * Order rules section — currently just the per-line purchase cap. Pure
 * presentation: parses the input, normalises empty → null (no limit),
 * clamps to positive integers, and lets the parent own the state.
 *
 * Left as its own section so adding future rules (min-per-order,
 * multipack, etc.) drops into the same slot without restructuring.
 */
function VisibilitySection({
  displayInCatalog,
  onChange
}: {
  readonly displayInCatalog: boolean
  readonly onChange: (next: boolean) => void
}): JSX.Element {
  return (
    <fieldset className="space-y-3 rounded-lg border border-border bg-bg-primary/40 p-4">
      <legend className="px-1 font-body text-body-sm font-semibold text-charcoal">
        Visibility
      </legend>
      <label className="flex items-start gap-3 cursor-pointer">
        <Checkbox
          checked={displayInCatalog}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>
          <span className="block font-body text-body-sm text-charcoal">Display in catalog</span>
          <span className="block font-body text-body-xs text-text-secondary mt-0.5">
            Show this product in Shop and in the tenant Build product picker.
            Uncheck for component-only products (parts of a larger product)
            that should stay hidden from customers but remain available to
            operators.
          </span>
        </span>
      </label>
    </fieldset>
  )
}

function OrderRulesSection({
  maxOrderQuantity,
  onChange,
  error
}: {
  readonly maxOrderQuantity: number | null
  readonly onChange: (next: number | null) => void
  readonly error: string | null
}): JSX.Element {
  return (
    <fieldset className="space-y-3 rounded-lg border border-border bg-bg-primary/40 p-4">
      <legend className="px-1 font-body text-body-sm font-semibold text-charcoal">
        Order rules
      </legend>
      <Field
        label="Max per order"
        helper="Hard cap on how many of this product can be in a single cart line. Leave blank for no limit."
        error={error}
      >
        <Input
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          placeholder="No limit"
          value={maxOrderQuantity === null ? '' : String(maxOrderQuantity)}
          hasError={error !== null}
          onChange={(e) => {
            const raw = e.target.value.trim()
            if (raw === '') {
              onChange(null)
              return
            }
            const n = Number(raw)
            if (!Number.isInteger(n) || n < 1) return
            onChange(n)
          }}
        />
      </Field>
    </fieldset>
  )
}

function CategoryAttributesSection({
  categoryName,
  attributes,
  values,
  customValues,
  fieldError,
  onSetValue,
  onToggleCustomValue
}: {
  readonly categoryName: string
  readonly attributes: ReadonlyArray<CategoryTreeAttribute>
  readonly values: Record<string, string>
  readonly customValues: Record<string, Set<string>>
  readonly fieldError: { field: string; message: string } | null
  readonly onSetValue: (attributeId: string, value: string) => void
  readonly onToggleCustomValue: (attributeId: string, valueId: string) => void
}): JSX.Element {
  if (attributes.length === 0) {
    return (
      <div className="border-t border-border pt-4 mt-2">
        <Alert tone="info">
          This category has no descriptive attributes. Variant axes (if any)
          render in the section below.
        </Alert>
      </div>
    )
  }
  return (
    <div className="border-t border-border pt-4 mt-2 space-y-4">
      <p className="font-body text-body-sm text-text-secondary">
        Fill in every attribute defined for <span className="font-mono">{categoryName}</span>.
      </p>
      {attributes.map((attr) => (
        <AttributeField
          key={attr.id}
          attribute={attr}
          value={values[attr.id] ?? ''}
          selectedCustomIds={customValues[attr.id] ?? new Set<string>()}
          error={fieldError?.field === `attr:${attr.id}` ? fieldError.message : null}
          onSetValue={(v) => onSetValue(attr.id, v)}
          onToggleCustom={(id) => onToggleCustomValue(attr.id, id)}
        />
      ))}
    </div>
  )
}

/**
 * AxesSection — grouped pickers for is_axes=1 CDT attributes + the
 * "Propagate variants" button. Visible only in edit mode (variants
 * are scoped to a saved product; can't propagate against a row that
 * doesn't exist yet). The button enables when the form's axes
 * selections drift from the product's currently-propagated set.
 */
function AxesSection({
  attributes,
  customValues,
  fieldError,
  onToggleCustomValue,
  propagateNeeded,
  propagateBusy,
  propagateError,
  onPropagate,
  propagateDisabled
}: {
  readonly attributes: ReadonlyArray<CategoryTreeAttribute>
  readonly customValues: Record<string, Set<string>>
  readonly fieldError: { field: string; message: string } | null
  readonly onToggleCustomValue: (attributeId: string, valueId: string) => void
  readonly propagateNeeded: boolean
  readonly propagateBusy: boolean
  readonly propagateError: string | null
  readonly onPropagate: () => void
  readonly propagateDisabled: boolean
}): JSX.Element {
  return (
    <div className="border-t-2 border-bamboo/40 pt-4 mt-2 space-y-4">
      <header className="flex items-baseline justify-between gap-3">
        <div>
          <h3 className="font-heading text-h4 text-charcoal">Variant axes</h3>
          <p className="font-body text-body-xs text-text-secondary mt-1">
            Pick which CDT values participate in the cartesian product.
            Click <span className="font-medium">Propagate variants</span> below
            to reconcile the variant table with these selections.
          </p>
        </div>
      </header>

      {attributes.map((attr) => (
        <AttributeField
          key={attr.id}
          attribute={attr}
          value=""
          selectedCustomIds={customValues[attr.id] ?? new Set<string>()}
          error={fieldError?.field === `attr:${attr.id}` ? fieldError.message : null}
          onSetValue={() => { /* axes are always custom-typed; the text input branch never fires */ }}
          onToggleCustom={(id) => onToggleCustomValue(attr.id, id)}
        />
      ))}

      {propagateError !== null ? (
        <Alert tone="error">{propagateError}</Alert>
      ) : null}

      <div className="flex items-center justify-end gap-3 pt-1">
        <span className="font-body text-body-xs text-text-secondary">
          {propagateNeeded
            ? 'Variants are out of sync with these selections.'
            : 'Variants match these selections.'}
        </span>
        <Button
          variant="primary"
          onClick={onPropagate}
          disabled={!propagateNeeded || propagateBusy || propagateDisabled}
        >
          {propagateBusy ? 'Propagating…' : 'Propagate variants'}
        </Button>
      </div>
    </div>
  )
}

function AttributeField({
  attribute,
  value,
  selectedCustomIds,
  error,
  onSetValue,
  onToggleCustom
}: {
  readonly attribute: CategoryTreeAttribute
  readonly value: string
  readonly selectedCustomIds: ReadonlySet<string>
  readonly error: string | null
  readonly onSetValue: (next: string) => void
  readonly onToggleCustom: (valueId: string) => void
}): JSX.Element {
  const isCustom = attribute.datatype_name === CUSTOM_DATATYPE_NAME
  const labelSuffix = (
    <span className="ml-2 text-text-secondary font-mono text-body-xs">
      ({isCustom && attribute.custom_data_type_name !== null
        ? `${attribute.datatype_name} · ${attribute.custom_data_type_name}`
        : attribute.datatype_name})
    </span>
  )

  if (isCustom) {
    if (attribute.custom_data_type_values.length === 0) {
      return (
        <Field label={<>{attribute.name}{labelSuffix}</>} error={error}>
          <Alert tone="warning">
            This custom data type has no values yet. Add some on the Custom
            Data Types screen before saving this product.
          </Alert>
        </Field>
      )
    }
    return (
      <Field label={<>{attribute.name}{labelSuffix}</>} error={error}>
        <div className="rounded-md border border-border bg-bg-primary p-2 max-h-48 overflow-y-auto space-y-1">
          {attribute.custom_data_type_values.map((v) => (
            <label
              key={v.id}
              className="flex items-center gap-2 px-2 py-1 hover:bg-parchment/40 rounded cursor-pointer"
            >
              <Checkbox
                checked={selectedCustomIds.has(v.id)}
                onChange={() => onToggleCustom(v.id)}
              />
              <span className="font-body text-body-sm text-text-primary">{v.value}</span>
            </label>
          ))}
        </div>
        <span className="block text-body-xs font-body text-text-secondary mt-1">
          {selectedCustomIds.size} selected · pick one or more
        </span>
      </Field>
    )
  }

  const shape = pickInputShape(attribute.datatype_name)
  return (
    <Field label={<>{attribute.name}{labelSuffix}</>} error={error}>
      {shape === 'textarea' ? (
        <Textarea
          value={value}
          rows={3}
          hasError={error !== null}
          onChange={(e) => onSetValue(e.target.value)}
          placeholder={attribute.description ?? ''}
          maxLength={VALUE_MAX}
        />
      ) : (
        <Input
          type={shape}
          value={value}
          hasError={error !== null}
          onChange={(e) => onSetValue(e.target.value)}
          placeholder={attribute.description ?? ''}
          maxLength={VALUE_MAX}
          inputMode={shape === 'number' ? 'decimal' : undefined}
          step={shape === 'number' ? 'any' : undefined}
        />
      )}
    </Field>
  )
}

function MediaSection({
  slots,
  atCap,
  error,
  fileInputRef,
  onPick,
  onDelete,
  onAltChange,
  onSetThumbnail,
  onMove
}: {
  readonly slots: ReadonlyArray<EffectiveSlot>
  readonly atCap: boolean
  readonly error: string | null
  readonly fileInputRef: React.RefObject<HTMLInputElement>
  readonly onPick: (files: FileList | null) => void
  readonly onDelete: (slot: EffectiveSlot) => void
  readonly onAltChange: (slot: EffectiveSlot, value: string) => void
  readonly onSetThumbnail: (slot: EffectiveSlot) => void
  readonly onMove: (slot: EffectiveSlot, dir: 'up' | 'down') => void
}): JSX.Element {
  return (
    <div className="border-t border-border pt-4 mt-2 space-y-3">
      <div className="flex items-center justify-between">
        <p className="font-body text-body-sm text-text-secondary">
          Media — {slots.length} of {MEDIA_MAX_ACTIVE}. Pick one as the
          thumbnail; reorder to control display order.
        </p>
        <Button
          variant="secondary"
          size="sm"
          disabled={atCap}
          onClick={() => fileInputRef.current?.click()}
        >
          + Add media
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT_TYPES}
          className="hidden"
          onChange={(e) => onPick(e.target.files)}
        />
      </div>
      {error !== null ? <Alert tone="error">{error}</Alert> : null}
      {slots.length === 0 ? (
        <p className="font-body text-body-sm text-text-secondary border border-dashed border-border rounded-md p-6 text-center">
          No media yet. Add at least one image to save the product.
        </p>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {slots.map((slot, idx) => (
            <MediaTile
              key={slot.key}
              slot={slot}
              canMoveUp={idx > 0}
              canMoveDown={idx < slots.length - 1}
              onDelete={() => onDelete(slot)}
              onAltChange={(v) => onAltChange(slot, v)}
              onSetThumbnail={() => onSetThumbnail(slot)}
              onMove={(dir) => onMove(slot, dir)}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function MediaTile({
  slot,
  canMoveUp,
  canMoveDown,
  onDelete,
  onAltChange,
  onSetThumbnail,
  onMove
}: {
  readonly slot: EffectiveSlot
  readonly canMoveUp: boolean
  readonly canMoveDown: boolean
  readonly onDelete: () => void
  readonly onAltChange: (value: string) => void
  readonly onSetThumbnail: () => void
  readonly onMove: (dir: 'up' | 'down') => void
}): JSX.Element {
  return (
    <li className="border border-border rounded-md p-3 bg-bg-primary space-y-2">
      <div className="flex items-start gap-3">
        <div className="w-24 h-24 bg-parchment/40 rounded overflow-hidden flex items-center justify-center shrink-0">
          {slot.isVideo ? (
            <span className="font-body text-body-xs text-text-secondary px-2 text-center">video · {formatBytes(slot.sizeBytes)}</span>
          ) : (
            <img
              src={slot.previewUrl}
              alt={slot.altText ?? slot.originalName ?? 'product media'}
              className="w-full h-full object-cover"
              crossOrigin="use-credentials"
            />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-body-xs text-text-secondary truncate">
            {slot.originalName ?? slot.contentType}
          </p>
          <p className="font-body text-body-xs text-text-secondary">
            {slot.width !== null && slot.height !== null
              ? `${slot.width}×${slot.height} · `
              : ''}
            {formatBytes(slot.sizeBytes)}
          </p>
          <div className="mt-1 flex items-center gap-2">
            {slot.isThumbnail ? (
              <span className="inline-block font-body text-body-xs px-2 py-0.5 rounded bg-forest text-text-light">
                Thumbnail
              </span>
            ) : (
              <button
                type="button"
                onClick={onSetThumbnail}
                className="font-body text-body-xs underline underline-offset-2 text-text-secondary hover:text-charcoal"
              >
                Set as thumbnail
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <button
            type="button"
            onClick={() => onMove('up')}
            disabled={!canMoveUp}
            className="font-body text-body-xs text-text-secondary disabled:opacity-30"
            aria-label="Move up"
          >
            ↑
          </button>
          <button
            type="button"
            onClick={() => onMove('down')}
            disabled={!canMoveDown}
            className="font-body text-body-xs text-text-secondary disabled:opacity-30"
            aria-label="Move down"
          >
            ↓
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="font-body text-body-xs text-terra hover:underline"
          >
            Delete
          </button>
        </div>
      </div>
      <Input
        value={slot.altText ?? ''}
        onChange={(e) => onAltChange(e.target.value)}
        placeholder="Alt text (optional)"
        maxLength={ALT_TEXT_MAX}
      />
    </li>
  )
}

function pickInputShape(
  datatypeName: string
): 'text' | 'number' | 'date' | 'textarea' {
  if (datatypeName === 'paragraph') return 'textarea'
  if (datatypeName === 'date') return 'date'
  if (datatypeName === 'number' || datatypeName === 'amount' || datatypeName === 'percentage') return 'number'
  return 'text'
}

